import { supabase } from './supabase';

export interface TournamentBracket {
  id: string;
  event_id: string;
  category_id: string;
  name: string;
  bracket_type: 'single_elimination' | 'double_elimination' | 'round_robin' | 'advanced_elimination' | 'qualifying' | 'elimination';
  total_rounds: number;
  current_round: number;
  status: 'pending' | 'active' | 'completed' | 'cancelled';
  tournament_phase: 'qualifying' | 'elimination' | 're_entry';
  qualifying_rounds: number;
  elimination_rounds: number;
  re_entry_rounds: number;
  max_re_entries_per_driver: number;
  qualifying_completed: boolean;
  bracket_style?: 'automatic' | 'manual';
  created_at: string;
  updated_at: string;
}

export interface TournamentMatch {
  id: string;
  bracket_id: string;
  round_number: number;
  match_number: number;
  match_type: 'qualifying' | 'elimination' | 're_entry';
  driver1_registration_id?: string;
  driver2_registration_id?: string;
  winner_registration_id?: string;
  loser_registration_id?: string;
  status: 'pending' | 'active' | 'completed' | 'cancelled' | 'bye';
  driver1_time?: number;
  driver2_time?: number;
  driver1_dial_in?: number;
  driver2_dial_in?: number;
  driver1_actual_time?: number;
  driver2_actual_time?: number;
  driver1_reaction_time?: number;
  driver2_reaction_time?: number;
  qualifying_position?: number;
  re_entry_count?: number;
  notes?: string;
  created_at: string;
  updated_at: string;
}

export interface QualifyingResult {
  id: string;
  bracket_id: string;
  registration_id: string;
  driver_id: string;
  qualifying_round: number;
  dial_in_time?: number;
  actual_time?: number;
  reaction_time?: number;
  total_time?: number;
  qualifying_position?: number;
  notes?: string;
  created_at: string;
  updated_at: string;
}

export interface DriverReEntry {
  id: string;
  bracket_id: string;
  registration_id: string;
  driver_id: string;
  re_entry_round: number;
  elimination_round: number;
  re_entry_count: number;
  created_at: string;
}

export interface TournamentResult {
  id: string;
  bracket_id: string;
  registration_id: string;
  driver_id: string;
  driver_profile_id?: string;
  elimination_round: number;
  final_position?: number;
  ladder_points: number;
  prize_money?: number;
  notes?: string;
  race_number?: string;
  created_at: string;
  updated_at: string;
}

// Removed DriverPoints interface - using tournament_results only for all leaderboards

export interface MatchResult {
  matchId: string;
  winnerRegistrationId: string;
  loserRegistrationId: string;
  winnerTime?: number;
  loserTime?: number;
  winnerReactionTime?: number;
  loserReactionTime?: number;
  notes?: string;
}

export interface QualifyingResultData {
  bracketId: string;
  registrationId: string;
  qualifyingRound: number;
  dialInTime?: number;
  actualTime?: number;
  reactionTime?: number;
}

// Bracket points are defined once in the database, by ladder_points_for_stage(): the stage a driver
// went out in, counted back from the final. Read them from `event_driver_points.position_points` or
// `tournament_results.ladder_points` rather than recomputing them here.

/**
 * Generates an advanced tournament bracket with qualifying and elimination phases
 * @param eventId - The event ID
 * @param categoryId - The category ID
 * @param bracketName - Optional bracket name (defaults to 'Main Bracket')
 * @param qualifyingRounds - Number of qualifying rounds (defaults to 1)
 * @param maxReEntries - Maximum re-entries per driver (defaults to 1)
 * @returns The created bracket ID
 */
export async function generateAdvancedTournamentBracket(
  eventId: string,
  categoryId: string,
  bracketName: string = 'Main Bracket',
  qualifyingRounds: number = 1,
  maxReEntries: number = 1
): Promise<string> {
  try {
    const { data, error } = await supabase.rpc('generate_advanced_tournament_bracket', {
      p_event_id: eventId,
      p_category_id: categoryId,
      p_bracket_name: bracketName,
      p_qualifying_rounds: qualifyingRounds,
      p_max_re_entries: maxReEntries
    });

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error generating advanced tournament bracket:', error);
    throw error;
  }
}

/**
 * Records a qualifying result for a driver
 * @param qualifyingData - The qualifying result data
 * @returns Success status
 */
export async function recordQualifyingResult(qualifyingData: QualifyingResultData): Promise<boolean> {
  try {
    const { data, error } = await supabase.rpc('record_qualifying_result', {
      p_bracket_id: qualifyingData.bracketId,
      p_registration_id: qualifyingData.registrationId,
      p_qualifying_round: qualifyingData.qualifyingRound,
      p_dial_in_time: qualifyingData.dialInTime,
      p_actual_time: qualifyingData.actualTime,
      p_reaction_time: qualifyingData.reactionTime
    });

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error recording qualifying result:', error);
    throw error;
  }
}

/**
 * Completes the qualifying phase and advances to elimination
 * @param bracketId - The bracket ID
 * @returns Success status
 */
export async function completeQualifyingPhase(bracketId: string): Promise<boolean> {
  try {
    const { data, error } = await supabase.rpc('complete_qualifying_phase', {
      p_bracket_id: bracketId
    });

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error completing qualifying phase:', error);
    throw error;
  }
}

/**
 * Advances tournament from qualifying to elimination phase
 * @param bracketId - The bracket ID
 * @returns Success status
 */
export async function advanceToEliminationPhase(bracketId: string): Promise<boolean> {
  try {
    const { data, error } = await supabase.rpc('advance_to_elimination_phase', {
      p_bracket_id: bracketId
    });

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error advancing to elimination phase:', error);
    throw error;
  }
}

/**
 * Handles driver re-entry into the tournament
 * @param bracketId - The bracket ID
 * @param registrationId - The registration ID
 * @param eliminationRound - The round where driver was eliminated
 * @returns Success status
 */
export async function reEnterDriver(
  bracketId: string,
  registrationId: string,
  eliminationRound: number
): Promise<boolean> {
  try {
    const { data, error } = await supabase.rpc('re_enter_driver', {
      p_bracket_id: bracketId,
      p_registration_id: registrationId,
      p_elimination_round: eliminationRound
    });

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error re-entering driver:', error);
    throw error;
  }
}

/**
 * Records a match result and updates the tournament bracket
 * @param matchResult - The match result data
 * @returns Success status
 */
export async function recordMatchResult(matchResult: MatchResult): Promise<boolean> {
  try {
    // Update the match with results
    const { error: matchError } = await supabase
      .from('tournament_matches')
      .update({
        winner_registration_id: matchResult.winnerRegistrationId,
        loser_registration_id: matchResult.loserRegistrationId,
        driver1_time: matchResult.winnerTime,
        driver2_time: matchResult.loserTime,
        driver1_reaction_time: matchResult.winnerReactionTime,
        driver2_reaction_time: matchResult.loserReactionTime,
        status: 'completed',
        notes: matchResult.notes
      })
      .eq('id', matchResult.matchId);

    if (matchError) throw matchError;

    // No tournament_results logic here!
    // Only update the match. Results/points will be finalized after the bracket is completed.

    return true;
  } catch (error) {
    console.error('Error recording match result:', error);
    throw error;
  }
}

/**
 * Gets tournament bracket with matches
 * @param bracketId - The bracket ID
 * @returns Tournament bracket and matches
 */
export async function getTournamentBracket(bracketId: string): Promise<{
  bracket: TournamentBracket;
  matches: TournamentMatch[];
}> {
  try {
    const { data: bracket, error: bracketError } = await supabase
      .from('tournament_brackets')
      .select('*')
      .eq('id', bracketId)
      .single();

    if (bracketError) throw bracketError;

    const { data: matches, error: matchesError } = await supabase
      .from('tournament_matches')
      .select('*')
      .eq('bracket_id', bracketId)
      .order('round_number', { ascending: true })
      .order('match_number', { ascending: true });

    if (matchesError) throw matchesError;

    return {
      bracket,
      matches: matches || []
    };
  } catch (error) {
    console.error('Error getting tournament bracket:', error);
    throw error;
  }
}

/**
 * Gets qualifying results for a bracket
 * @param bracketId - The bracket ID
 * @returns Qualifying results with driver information
 */
export async function getQualifyingResults(bracketId: string): Promise<QualifyingResult[]> {
  try {
    // First get the qualifying results without the problematic join
    const { data, error } = await supabase
      .from('qualifying_results')
      .select('*')
      .eq('bracket_id', bracketId)
      .order('qualifying_round', { ascending: true })
      .order('qualifying_position', { ascending: true });

    if (error) throw error;
    return data || [];
  } catch (error) {
    console.error('Error getting qualifying results:', error);
    throw error;
  }
}

/**
 * Gets driver re-entries for a bracket
 * @param bracketId - The bracket ID
 * @returns Driver re-entry information
 */
export async function getDriverReEntries(bracketId: string): Promise<DriverReEntry[]> {
  try {
    const { data, error } = await supabase
      .from('driver_re_entries')
      .select(`
        *,
        event_registrations!inner(
          user_id,
          users!inner(
            full_name,
            email
          )
        )
      `)
      .eq('bracket_id', bracketId)
      .order('re_entry_round', { ascending: true });

    if (error) throw error;
    return data || [];
  } catch (error) {
    console.error('Error getting driver re-entries:', error);
    throw error;
  }
}

/**
 * Gets tournament results for a bracket
 * @param bracketId - The bracket ID
 * @returns Tournament results with driver information
 */
export async function getTournamentResults(bracketId: string): Promise<TournamentResult[]> {
  try {
    const { data, error } = await supabase
      .from('tournament_results')
      .select(`
        *,
        event_registrations!inner(
          user_id,
          temporary_user_id,
          driver_profile_id,
          users!inner(
            full_name,
            email
          ),
          temporary_user(
            name,
            race_number
          )
        ),
        driver_profiles(
          vehicle_info
        )
      `)
      .eq('bracket_id', bracketId)
      .order('final_position', { ascending: true })
      .order('elimination_round', { ascending: false });

    if (error) throw error;
    
    // Process the data to include race numbers using the direct driver_profile_id
    const processedData = (data || []).map(result => {
      let raceNumber = null;
      
      // Try to get race number from the direct driver_profile_id
      if (result.driver_profile_id && result.driver_profiles && result.driver_profiles.length > 0) {
        const profile = result.driver_profiles.find(p => p.id === result.driver_profile_id);
        if (profile) {
          raceNumber = profile.vehicle_info?.raceNumber;
        }
      }
      
      // If no race number from profile, try temporary user
      if (!raceNumber && result.event_registrations.temporary_user) {
        raceNumber = result.event_registrations.temporary_user.race_number;
      }
      
      return {
        ...result,
        race_number: raceNumber
      };
    });
    
    return processedData;
  } catch (error) {
    console.error('Error getting tournament results:', error);
    throw error;
  }
}

// Removed getDriverPoints and getDriverLeaderboard functions - using tournament_results only for all leaderboards

/**
 * Gets all tournament brackets for an event
 * @param eventId - The event ID
 * @returns Array of tournament brackets
 */
export async function getEventTournamentBrackets(eventId: string): Promise<TournamentBracket[]> {
  try {
    const { data, error } = await supabase
      .from('tournament_brackets')
      .select(`
        *,
        event_categories!inner(
          name,
          vehicle_type
        )
      `)
      .eq('event_id', eventId)
      .order('created_at', { ascending: true });

    if (error) throw error;
    return data || [];
  } catch (error) {
    console.error('Error getting event tournament brackets:', error);
    throw error;
  }
}

/**
 * Calculates the number of rounds needed for a given number of participants
 * @param participantCount - Number of participants
 * @returns Number of rounds needed
 */
export function calculateTotalRounds(participantCount: number): number {
  return Math.ceil(Math.log2(participantCount));
}

/**
 * Gets the next match for a driver in the tournament
 * @param bracketId - The bracket ID
 * @param driverRegistrationId - The driver's registration ID
 * @returns Next match information or null if no next match
 */
export async function getNextMatch(
  bracketId: string,
  driverRegistrationId: string
): Promise<TournamentMatch | null> {
  try {
    const { data, error } = await supabase
      .from('tournament_matches')
      .select('*')
      .eq('bracket_id', bracketId)
      .or(`driver1_registration_id.eq.${driverRegistrationId},driver2_registration_id.eq.${driverRegistrationId}`)
      .eq('status', 'pending')
      .order('round_number', { ascending: true })
      .order('match_number', { ascending: true })
      .limit(1)
      .single();

    if (error && error.code !== 'PGRST116') throw error; // PGRST116 = no rows returned
    return data;
  } catch (error) {
    console.error('Error getting next match:', error);
    throw error;
  }
}

/**
 * Validates if a match result is valid
 * @param matchResult - The match result to validate
 * @returns Validation result with any errors
 */
export function validateMatchResult(matchResult: MatchResult): {
  isValid: boolean;
  errors: string[];
} {
  const errors: string[] = [];

  if (!matchResult.matchId) {
    errors.push('Match ID is required');
  }

  if (!matchResult.winnerRegistrationId) {
    errors.push('Winner registration ID is required');
  }

  if (!matchResult.loserRegistrationId) {
    errors.push('Loser registration ID is required');
  }

  if (matchResult.winnerRegistrationId === matchResult.loserRegistrationId) {
    errors.push('Winner and loser cannot be the same driver');
  }

  if (matchResult.winnerTime !== undefined && matchResult.winnerTime <= 0) {
    errors.push('Winner time must be positive');
  }

  if (matchResult.loserTime !== undefined && matchResult.loserTime <= 0) {
    errors.push('Loser time must be positive');
  }

  return {
    isValid: errors.length === 0,
    errors
  };
} 